import { Component, Inject, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '../../../../services/auth/auth.service';
import { Solicitud } from '../../../../interfaces/interface-bakend/ofertas-laborales/solicitudes.interface';
import { SolicitudesService } from '../../../../services/ofertas-laborales/solicitudes.service';
import { User } from '../../../../interfaces/interface-bakend/seguridad/users/user.interface';
import { ActividadesService } from '../../../../services/ofertas-laborales/actividades.service';
import { PaisesService } from '../../../../services/shared/paises.service';
import { Actividad } from '../../../../interfaces/interface-bakend/ofertas-laborales/actividad.interface';
import { PaisDisponible } from '../../../../interfaces/interface-bakend/shared/paises/pais-disponible/pais-disponible.interface';
import {
    APP_CONFIG_OFERTAS,
    IAppOfertas,
} from '../../../../app.config/app.config-ofertas';
import { UserEntreUserService } from '@services/shared/user-entre-user.service';
import { MediaOfertaLaboralFileUserPipe } from '@pipes/ofertas-laborales/mediaOfertaLaboralFileUser.Pipe';
import { MatDialog } from '@angular/material/dialog';
import { OfertasVerPdfComponent } from '@components/ofertas-laborales/common/ofertas-ver-pdf/ofertas-ver-pdf.component';
import { estadoChipClase } from '../../common/helpers/estado.helper';

const FileSaver = require('file-saver');

@Component({
    selector: 'app-usuario-solicitudes',
    templateUrl: './usuario-solicitudes.component.html',
    styleUrls: ['./usuario-solicitudes.component.scss'],
})
export class UsuariosSolicitudesComponent implements OnInit {
    usuario: User;
    flagLoaded = false;

    /* Lista + filtro + paginación */
    elementos: any[] = [];
    elementosFiltrados: any[] = [];
    elementosPagina: any[] = [];
    textoFiltro = '';
    paginaActual = 1;
    pageSize = 10;
    pageSizeOptions: number[] = [5, 10, 25, 100];
    totalPaginas = 1;
    skeletonRows = [1, 2, 3, 4, 5, 6];

    private readonly FECHA_CORTE_FILTRO = new Date('2026-08-04T00:00:00.000Z');

    constructor(
        @Inject(APP_CONFIG_OFERTAS) public iAppOfertas: IAppOfertas,
        private solicitudesService: SolicitudesService,
        private actividadesService: ActividadesService,
        private paisesService: PaisesService,
        private authservice: AuthService,
        private router: Router,
        private userEntreUserService: UserEntreUserService,
        public dialog: MatDialog
    ) {}

    async ngOnInit(): Promise<void> {
        await this.loadDataPage();
    }

    // =====================================================
    // Datos
    // =====================================================

    async loadDataPage(): Promise<void> {
        this.usuario = await this.authservice.getUserLogon();
        await this.getSolicitudesByUser(this.usuario._id);
    }

    async getSolicitudesByUser(id: string): Promise<void> {
        this.flagLoaded = false;
        try {
            const solds = await this.solicitudesService.getSolicitudesByUser(id);

            const solicitudesFiltradas = (solds ?? []).filter((sol) => {
                const numberState =
                    (sol as any)?.lastState_numberState ??
                    sol?.lastState?.numberState ??
                    -1;
                return this.debeIncluirSolicitud(sol, numberState);
            });

            this.elementos = await this.formatDataTable(solicitudesFiltradas);
            this.paginaActual = 1;
            this.recalcular();
        } finally {
            this.flagLoaded = true;
        }
    }

    async formatDataTable(solicitudes: Solicitud[]): Promise<any[]> {
        const beneficiaries = await this.userEntreUserService.findGrupoFamiliar(
            this.usuario._id
        );

        return Promise.all(
            solicitudes.map(async (solicitudItem) => {
                const familiarAcargo = beneficiaries.find(
                    (beneficiary) =>
                        beneficiary.adicional?._id === solicitudItem.beneficiario
                );
                const files = await this.getArrayImgsAndFile(solicitudItem.adjuntos);

                return {
                    pais: solicitudItem.pais.name_es,
                    tramite: solicitudItem.actividad.nombre,
                    _nro: solicitudItem._nro,
                    fecha: solicitudItem.createdAt,
                    progreso: solicitudItem.lastState.numberState,
                    ver: solicitudItem,
                    beneficiarioNombre:
                        familiarAcargo?.adicional?.fullName ||
                        solicitudItem?.usuario?.fullName,
                    beneficiarioParentezco:
                        familiarAcargo?.parentesco || 'TITULAR',
                    file: files,
                };
            })
        );
    }

    async getArrayImgsAndFile(adjuntos: string[]): Promise<string[]> {
        return (adjuntos ?? []).filter((element) => element.includes('ORM'));
    }

    private debeIncluirSolicitud(solicitud: any, numberState: number): boolean {
        const createdAt = solicitud?.createdAt
            ? new Date(solicitud.createdAt)
            : null;

        if (createdAt && createdAt >= this.FECHA_CORTE_FILTRO) {
            return numberState !== 0;
        }

        return numberState !== 0 && numberState !== 1;
    }

    // =====================================================
    // Filtro + paginación
    // =====================================================

    aplicarFiltro(event: Event): void {
        this.textoFiltro = (event.target as HTMLInputElement).value;
        this.paginaActual = 1;
        this.recalcular();
    }

    cambiarPagina(pagina: number): void {
        if (pagina < 1 || pagina > this.totalPaginas) {
            return;
        }
        this.paginaActual = pagina;
        this.recalcular();
    }

    onPageSizeChange(event: Event): void {
        this.pageSize = Number((event.target as HTMLSelectElement).value) || 10;
        this.paginaActual = 1;
        this.recalcular();
    }

    recalcular(): void {
        const texto = this.textoFiltro.trim().toLowerCase();
        let filtrados = this.elementos;

        if (texto) {
            filtrados = this.elementos.filter((elemento) => {
                const haystack = [
                    elemento._nro,
                    elemento.tramite,
                    elemento.pais,
                    elemento.beneficiarioNombre,
                    elemento.beneficiarioParentezco,
                    this.getEstadoNombre(elemento.progreso),
                ]
                    .filter((valor) => valor != null && valor !== '')
                    .join(' ')
                    .toLowerCase();
                return haystack.includes(texto);
            });
        }

        this.elementosFiltrados = filtrados;
        this.totalPaginas = Math.max(
            1,
            Math.ceil(filtrados.length / this.pageSize)
        );

        if (this.paginaActual > this.totalPaginas) {
            this.paginaActual = 1;
        }

        const inicio = (this.paginaActual - 1) * this.pageSize;
        this.elementosPagina = filtrados.slice(inicio, inicio + this.pageSize);
    }

    // =====================================================
    // Presentación
    // =====================================================

    getEstadoChipClase(stateNumber: number): string {
        return estadoChipClase(stateNumber);
    }

    getEstadoNombre(stateNumber: number): string {
        const estado = this.iAppOfertas.selectEstados.find(
            (e) => e.number === stateNumber
        );
        return estado ? estado.name : '';
    }

    trackByElemento(index: number, elemento: any): string {
        return elemento?.ver?._id ?? String(index);
    }

    // =====================================================
    // Navegación y descargas
    // =====================================================

    async verSolicitud(elemento: any): Promise<void> {
        const solicitud: Solicitud = elemento.ver;
        await this.setActividad(solicitud.actividad);
        await this.setPaisDisponible(solicitud.actividad.paisDisponible._id);
        await this.solicitudesService.setSolicitudSeleccionada(solicitud);
        this.router.navigateByUrl('/solicitud');
    }

    setActividad(actividad: Actividad): void {
        this.actividadesService.setActividadSeleccionada(actividad);
    }

    async setPaisDisponible(id: string): Promise<void> {
        await this.paisesService
            .getPaisDisponibleFinOne(id)
            .then((pais: PaisDisponible) => {
                this.paisesService.setPaisSeleccionado(pais);
            });
    }

    download(idUsuario: string, adjunto: string, nombre: string): void {
        FileSaver.saveAs(
            this.mediaOfertaLaboralFileUser(idUsuario, adjunto),
            nombre
        );
    }

    mediaOfertaLaboralFileUser(idUsuario: string, adjunto: string): string {
        const mediaOfertaLaboralFileUser = new MediaOfertaLaboralFileUserPipe();
        return mediaOfertaLaboralFileUser.transform(idUsuario, adjunto);
    }

    openDialogPdf(
        idUsuario: string,
        adjunto: string,
        tituloDocumento: string
    ): void {
        const mediaOfertaLaboralFileUser = new MediaOfertaLaboralFileUserPipe();
        const path = mediaOfertaLaboralFileUser.transform(idUsuario, adjunto);

        this.dialog.open(OfertasVerPdfComponent, {
            width: '800px',
            height: '800px',
            disableClose: true,
            panelClass: 'ds-dialog',
            data: {
                path,
                tituloDocumento,
            },
        });
    }
}
